// @ts-strict-ignore
import React, {useState} from 'react';
import isEqual from 'lodash/isEqual';
import produce from 'immer';

import {SelectTheme} from 'common/components/select/constants';
import {SelectReact} from 'common/components/select';
import {TooltipReact} from 'common/components/tooltip';
import {Bucket, BucketSizeReact} from 'common/widgets/bucket-sizes';
import {Directions} from 'common/components/drop-menu/types';
import {ButtonSize} from 'common/components/button/constants';
import {DisplaySize, Placement} from 'common/components/tooltip/types';
import {TimeUnit} from 'common/widgets/date-range-picker/models/types';
import {useTranslation} from 'common/i18n/hooks';
import onImg from 'common/assets/images/retention-criteria/on.webp';
import onOrAfterImg from 'common/assets/images/retention-criteria/on_or_after.webp';
import onOrBeforeImg from 'common/assets/images/retention-criteria/on_or_before.webp';
import streakImg from 'common/assets/images/retention-criteria/streak.webp';
import {ChartType, MetricType, RetentionAlignmentType, RetentionUnboundedModeType} from 'common/types/reports/bookmark';
import {getCurrentMoment} from 'common/util/date';
import {useCurrentProject} from 'common/contexts/react/current-project-context';
import {SectionItem} from 'common/components/items-menu/types';
import {MetricEntry} from 'common/query-builder/query-entry-section/types';
import {SwitchChangeEvent, SwitchEl} from 'common/components/switch';
import {MetricBehavior} from 'common/report/insights/util/types';

import type {RowProps} from '../catalog';
import styles from './index.module.styl';

export const CUSTOM_RETENTION_UNIT = `custom`;

const commonTooltipAttrs = {
  size: DisplaySize.Small,
};

/**
 * Renders and manages the retention criteria portion of a retention behavior
 * It manages things like on, on or after retention, retention unit selection, etc...
 */
export function RetentionCriteria(props: RowProps) {
  const {t, formatDate} = useTranslation();
  const currentProject = useCurrentProject();

  const [isCustomBucketOpen, setIsCustomBucketOpen] = useState(false);

  const unboundedMode = props.entry.behavior.retentionUnboundedMode;
  const retentionUnit = props.entry.behavior.retentionUnit;
  const bucketSizes = props.entry.behavior.retentionCustomBucketSizes ?? [];
  const retentionAlignmentType = props.entry.behavior.retentionAlignmentType;
  const disableCohortize = props.entry.behavior.disableCohortize;
  const chartType = props.currentChartType;

  const bucketLength = bucketSizes.length;
  const selectedRetentionUnitValue = bucketLength !== 0 ? CUSTOM_RETENTION_UNIT : retentionUnit;

  const updateBehavior = (updates: Partial<MetricBehavior>) => {
    props.updateEntry(
      produce(props.entry, (draft) => {
        Object.assign(draft.behavior, updates);
      }),
    );
  };

  /** Sets the retention count type to On,, On or After, Strike, etc... */
  const handleChangeCountingMethodSelector = (value: RetentionUnboundedModeType) =>
    updateBehavior({retentionUnboundedMode: value});

  /** Sets the retention intervals */
  const handleRetentionUnitChanged = ({
    retentionUnit: updatedRetentionUnit,
    bucketSizes: updatedBucketSizes,
  }: {
    retentionUnit?: TimeUnit;
    bucketSizes?: number[];
  }) => {
    const behaviorModifier = (entry: MetricEntry) =>
      produce(entry, (draft) => {
        draft.behavior.retentionUnit = updatedRetentionUnit ?? draft.behavior.retentionUnit;
        draft.behavior.retentionCustomBucketSizes = updatedBucketSizes ?? [];

        // if retention unit changed, set bucket index to 0
        if (!isEqual(draft.behavior.retentionUnit, updatedRetentionUnit)) {
          draft.measurement.retentionBucketIndex = 0;
        }
      });
    // TODO(CC): When the chart type is retention curve, changing the retention unit or bucket sizes
    // of a retention metric changes the retention units of all the other existing retention metrics.
    // Tune this later when we find a better solution to handle this.
    if (props.currentChartType === ChartType.RetentionCurve) {
      const entries = props.getEntries() as MetricEntry[];
      entries.forEach((entry, index) => {
        if (props.entry.behavior.type === MetricType.Retention) {
          props.updateEntry(behaviorModifier(entry), {index});
        }
      });
    }

    // updates rendered retention metric
    props.updateEntry(behaviorModifier(props.entry));
  };

  const isCalendarAlignment = (alignmentType: RetentionAlignmentType) => {
    return alignmentType !== RetentionAlignmentType.Birth;
  };

  const getBucketLabel = (retentionBucketSizes: number[]) => {
    let previousBucket = 0;
    const label = retentionBucketSizes
      .map((bucket) => {
        const currentBucket = previousBucket + bucket;
        const retLabel = bucket === 1 ? `${currentBucket - 1}` : `${previousBucket}-${currentBucket - 1}`;
        previousBucket = currentBucket;
        return retLabel;
      })
      .join(` / `);
    return label;
  };

  const getLabel = (retentionBucketSizes: number[], bucketUnit: TimeUnit, alignmentType: RetentionAlignmentType) => {
    const map = isCalendarAlignment(alignmentType)
      ? {
          [TimeUnit.Day]: t(`query-builder.retention.interval.label.calendar-day`),
          [TimeUnit.Week]: t(`query-builder.retention.interval.label.calendar-week`),
          [TimeUnit.Month]: t(`query-builder.retention.interval.label.calendar-month`),
        }
      : {
          [TimeUnit.Day]: t(`query-builder.retention.interval.label.day`),
          [TimeUnit.Week]: t(`query-builder.retention.interval.label.week`),
          [TimeUnit.Month]: t(`query-builder.retention.interval.label.month`),
        };

    const label = getBucketLabel(retentionBucketSizes);
    const unit = map[bucketUnit];
    return `${unit} ${label}`;
  };

  const getUnitSelectorTooltip = () =>
    bucketSizes && bucketSizes.length
      ? {
          text: `Returning users on ${retentionUnit}s: ${getBucketLabel(bucketSizes)}`,
          ...commonTooltipAttrs,
        }
      : commonTooltipAttrs;

  const getRetentionUnboundedOptions = (): Array<SectionItem<RetentionUnboundedModeType>> => [
    {
      label: () => t(`query-builder.retention.criteria.on-or-after`),
      onSelect: () => handleChangeCountingMethodSelector(`carry_back`),
      isSelected: unboundedMode === `carry_back`,
      tooltip: {
        text: () => t(`query-builder.retention.criteria.tooltip.on-or-after`),
        'image-src': onOrAfterImg,
        ...commonTooltipAttrs,
      },
    },
    {
      label: () => t(`query-builder.retention.criteria.on`),
      isSelected: !unboundedMode || unboundedMode === `none`,
      onSelect: () => handleChangeCountingMethodSelector(`none`),
      tooltip: {
        text: () => t(`query-builder.retention.criteria.tooltip.on`),
        'image-src': onImg,
        ...commonTooltipAttrs,
      },
    },
    {
      label: () => t(`query-builder.retention.criteria.on-or-before`),
      onSelect: () => handleChangeCountingMethodSelector(`carry_forward`),
      isSelected: unboundedMode === `carry_forward`,
      tooltip: {
        text: () => t(`query-builder.retention.criteria.tooltip.on-or-before`),
        'image-src': onOrBeforeImg,
        ...commonTooltipAttrs,
      },
    },
    {
      label: () => t(`query-builder.retention.criteria.consecutive-forward`),
      onSelect: () => handleChangeCountingMethodSelector(`consecutive_forward`),
      isSelected: unboundedMode === `consecutive_forward`,
      tooltip: {
        text: () => t(`query-builder.retention.criteria.tooltip.consecutive-forward`),
        'image-src': streakImg,
        ...commonTooltipAttrs,
      },
    },
  ];

  const getAdvancedMenuOptions = (): SectionItem[] => {
    const timezone = currentProject.get()?.value?.timezone;
    const today = formatDate(getCurrentMoment({timezone}), {
      month: `short`,
      day: `numeric`,
    });
    const tomorrow = formatDate(getCurrentMoment({timezone}).add(1, `days`), {
      month: `short`,
      day: `numeric`,
    });

    const items: SectionItem[] = [
      {
        label: t(`retention.group-by-date`),
        tooltip: {
          placement: Placement.Top,
          'max-width': `185px`,
          text: t(`retention.group-by-date-tooltip`, {today, tomorrow}),
        },
        actionsRenderer: () =>
          SwitchEl({
            attrs: {
              on: retentionAlignmentType === RetentionAlignmentType.IntervalStart,
            },
            on: {
              change: (ev: SwitchChangeEvent) =>
                updateBehavior({
                  retentionAlignmentType: ev.detail.on
                    ? RetentionAlignmentType.IntervalStart
                    : RetentionAlignmentType.Birth,
                }),
            },
          }),
      },
    ];

    if (chartType !== ChartType.Line) {
      items.push({
        label: t(`retention.cohortize`),
        tooltip: {
          placement: Placement.Top,
          'max-width': `185px`,
          text: t(`retention.cohortize-tooltip`),
        },
        actionsRenderer: () =>
          SwitchEl({
            attrs: {
              on: disableCohortize !== true,
            },
            on: {
              change: (ev: SwitchChangeEvent) => updateBehavior({disableCohortize: !ev.detail.on}),
            },
          }),
      });
    }

    return items;
  };

  return (
    <div className={styles.retentionCriteria}>
      {/* selects retention on, on or after, on or before, streak */}
      <SelectReact
        alwaysShowCaret
        className={`${styles.select} ${styles.methodSelect}`}
        menuWidth={-1}
        theme={SelectTheme.Secondary}
        size={ButtonSize.Small}
        sections={[
          {
            items: getRetentionUnboundedOptions(),
          },
        ]}
      />

      {/* retention bucket unit selection, on each day, week, month, etc... */}
      <SelectReact
        alwaysShowCaret
        className={styles.select}
        elref='retUnitSelector'
        label={getLabel(bucketSizes, retentionUnit, retentionAlignmentType)}
        theme={SelectTheme.Secondary}
        size={ButtonSize.Small}
        menuWidth={-1}
        tooltip={getUnitSelectorTooltip()}
        sections={[
          {
            items: [
              {
                value: TimeUnit.Day,
                onSelect: () => handleRetentionUnitChanged({retentionUnit: TimeUnit.Day}),
                isSelected: selectedRetentionUnitValue === TimeUnit.Day,
                label: t(`query-builder.retention.interval.day`),
                tooltip: {
                  text: t(`query-builder.retention.interval.tooltip.day`),
                  ...commonTooltipAttrs,
                },
              },
              {
                value: TimeUnit.Week,
                onSelect: () => handleRetentionUnitChanged({retentionUnit: TimeUnit.Week}),
                isSelected: selectedRetentionUnitValue === TimeUnit.Week,
                label: t(`query-builder.retention.interval.week`),
                tooltip: {
                  text: t(`query-builder.retention.interval.tooltip.week`),
                  ...commonTooltipAttrs,
                },
              },
              {
                value: TimeUnit.Month,
                onSelect: () => handleRetentionUnitChanged({retentionUnit: TimeUnit.Month}),
                isSelected: selectedRetentionUnitValue === TimeUnit.Month,
                label: t(`query-builder.retention.interval.month`),
                tooltip: {
                  text: t(`query-builder.retention.interval.tooltip.month`),
                  ...commonTooltipAttrs,
                },
              },
              {
                value: CUSTOM_RETENTION_UNIT,
                isSelected: selectedRetentionUnitValue === CUSTOM_RETENTION_UNIT,
                label: t(`query-builder.retention.interval.custom`),
                onSelect: () => setIsCustomBucketOpen(true),
                tooltip: {
                  text: t(`query-builder.retention.interval.tooltip.custom`),
                  ...commonTooltipAttrs,
                },
              },
            ],
          },
        ]}
      />

      {/* retention custom bucket selector */}
      {isCustomBucketOpen && (
        <TooltipReact text={`${t(`query-builder.retention.interval.custom-menu.returning-users`)}: ${bucketSizes}`} />
      )}
      {isCustomBucketOpen && (
        <BucketSizeReact
          className={styles.bucketSizes}
          // @ts-ignore
          header={t(`query-builder.retention.interval.custom-menu.title`)}
          bucketUnit={retentionUnit}
          bucketSizes={bucketSizes}
          onApply={(ev: CustomEvent<{bucketUnit: TimeUnit; buckets: Bucket[]}>) => {
            setIsCustomBucketOpen(false);
            handleRetentionUnitChanged({
              retentionUnit: ev.detail.bucketUnit,
              bucketSizes: ev.detail.buckets.map((bucket) => bucket.interval),
            });
          }}
          onCancel={() => setIsCustomBucketOpen(false)}
        />
      )}

      {/* advanced dropdown to show options like cohortize or group by dates */}
      <SelectReact
        className={`${styles.select} ${styles.advancedMenu}`}
        slot='actions'
        theme={SelectTheme.Tertiary}
        size={ButtonSize.Small}
        alwaysShowCaret
        selectedOption={t(`query-builder.advanced`)}
        menuAlignment={Directions.Right}
        tooltip={{placement: Placement.Right}}
        experimentalPositioning
        menuWidth={-1}
        sections={[
          {
            items: getAdvancedMenuOptions(),
          },
        ]}
      />
    </div>
  );
}
